import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Typography, Row, Col, Spin, Alert, Button } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, CheckCircleOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import badgeImage from "Common/hooks/assets/images/badge.jpg";
import signature1 from "Common/hooks/assets/images/signature1.jpg";
import signature2 from "Common/hooks/assets/images/signature2.jpg";
import 'PlatForms/SelfLMS/Components/Credentials/Certificate.css'
import axios from 'axios';

const { Title, Text } = Typography;

const PublicCertificatePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Get sharingId from URL query parameters
  const getSharingId = () => {
    const params = new URLSearchParams(location.search);
    return params.get('sharingId') || params.get('SharingId');
  };

  const sharingId = getSharingId();

  useEffect(() => {
    console.log("PublicCertificatePage Mounted");
    console.log("Current URL:", window.location.href);
    console.log("Location Search:", location.search);
    console.log("Extracted SharingId:", sharingId);

    const fetchCertificate = async () => {
      try {
        setLoading(true);

        if (!sharingId) {
          console.log("No SharingId found");
          setError("No certificate ID provided in URL");
          setLoading(false);
          return;
        }

        console.log("Fetching certificate data...");

        // Use the correct API base URL for your environment
        const API_BASE_URL = process.env.REACT_APP_API_URL;
        const url = `${API_BASE_URL}/indv/Certification/Get-By-SharingId/${sharingId}`;

        console.log("API Request URL:", url);

        const response = await axios.get(url, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          timeout: 15000,
        });

        console.log("Certificate data received successfully!");
        console.log("Certificate Data:", response.data);

        setCertificate(response.data);
        setError(null);

      } catch (err) {
        console.error("Error fetching certificate:", err);
        toast.error(err?.response?.data?.message || err?.message || "Failed to load certificate");

        if (err.code === "ERR_NETWORK") {
          setError("Network error: Unable to connect to the certificate server. Please check your internet connection.");
        } else if (err.response) {
          const status = err.response.status;
          console.log("Server Response Status:", status);

          if (status === 404) {
            setError("Certificate not found. The certificate may have been revoked or the URL is incorrect.");
          } else if (status === 403) {
            setError("Access denied. This certificate is not publicly available.");
          } else if (status >= 500) {
            setError("Server error. Please try again later.");
          } else {
            setError(`Server error (${status}): ${err.response.data?.message || 'Please check the certificate URL'}`);
          }
        } else if (err.request) {
          setError("No response from server. Please check if the server is running and accessible.");
        } else {
          setError(`Unexpected error: ${err.message}`);
        }

        setCertificate(null);
      } finally {
        console.log("Fetch operation completed");
        setLoading(false);
      }
    };

    // Add a small delay to ensure the component is properly mounted
    const timer = setTimeout(() => {
      if (sharingId) {
        fetchCertificate();
      } else {
        setError("No certificate ID found in the URL. Please check the link.");
        setLoading(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [sharingId, location.search]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error("Date formatting error:", error);
      return "Invalid Date";
    }
  };

  const handleDownloadPDF = async () => {
    if (!certificate) {
      toast.error('Certificate data not loaded');
      return;
    }

    try {
      setDownloading(true);
      toast.info('Generating PDF...');

      const certificateElement = document.getElementById("badge-container");

      if (!certificateElement) {
        toast.error('Certificate element not found');
        return;
      }

      const canvas = await html2canvas(certificateElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      // Fit the badge into a standard page (aspect preserved, centered) instead
      // of sizing the page to the raw canvas (which produced a huge page).
      const ratio = Math.min(pageWidth / imgProps.width, pageHeight / imgProps.height);
      const drawW = imgProps.width * ratio;
      const drawH = imgProps.height * ratio;
      pdf.addImage(imgData, "PNG", (pageWidth - drawW) / 2, (pageHeight - drawH) / 2, drawW, drawH);
      const fileName = `${(certificate.title || "Certificate").replace(/\s+/g, "_")}_Badge.pdf`;
      pdf.save(fileName);

      toast.success('Badge PDF downloaded successfully!');

    } catch (err) {
      console.error("PDF download error:", err);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!certificate) return;

    const link = window.location.href;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Badge link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  // Updated handleBack function to navigate to credentials page
  const handleBack = () => {
    navigate('/credentials');
  };

  const handleRetry = () => {
    window.location.reload();
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #FFFDF8 0%, #F5EFE2 100%)"
      }}>
        <Spin size="large" />
        <Text style={{ marginTop: 16, color: "#666" }}>Loading badge...</Text>
        {sharingId && (
          <Text style={{ marginTop: 8, color: "#999", fontSize: "12px" }}>
            Fetching badge ID: {sharingId}
          </Text>
        )}
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #FFFDF8 0%, #F5EFE2 100%)",
        padding: "20px"
      }}>
        <Card style={{ maxWidth: 500, width: "100%", textAlign: 'center' }}>
          <Alert
            message="Badge Verification Failed"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">
              Badge ID: {sharingId || 'Not provided'}
            </Text>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <Button type="primary" onClick={handleRetry}>
              Try Again
            </Button>
            <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
              Back to Credentials
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Show certificate not found state
  if (!certificate) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #FFFDF8 0%, #F5EFE2 100%)"
      }}>
        <Card style={{ maxWidth: 500, width: "100%" }}>
          <Alert
            message="Badge Not Found"
            description="The requested badge could not be loaded. Please verify the URL and try again."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Button type="primary" onClick={handleBack} icon={<ArrowLeftOutlined />} block>
            Back to Credentials
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px",
        background: "linear-gradient(135deg, #FFFDF8 0%, #F5EFE2 100%)",
        minHeight: "100vh",
      }}
    >
      {/* Action Buttons - Compact */}
      <div
        style={{
          width: "100%",
          maxWidth: "800px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Button
          type="default"
          onClick={handleBack}
          icon={<ArrowLeftOutlined />}
          style={{ padding: "8px 16px" }}
        >
          Back
        </Button>

        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            type="primary"
            onClick={handleDownloadPDF}
            loading={downloading}
            disabled={downloading}
            icon={<DownloadOutlined />}
            style={{ padding: "8px 16px", background: "#9A6B0E", borderColor: "#9A6B0E" }}
          >
            {downloading ? 'Generating...' : 'Download'}
          </Button>

          <Button
            type="default"
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={handleCopyLink}
            style={{ padding: "8px 16px" }}
          >
            {copied ? "Copied!" : "Copy Link"}
          </Button>
        </div>
      </div>

      {/* Verification Badge - Compact */}
      <div style={{
        display: "flex",
        alignItems: "center",
        padding: "6px 12px",
        background: "#f6ffed",
        border: "1px solid #b7eb8f",
        borderRadius: "16px",
        marginBottom: "15px"
      }}>
        <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 6 }} />
        <Text style={{ color: "#52c41a", fontWeight: 500, fontSize: "14px" }}>Verified Badge</Text>
      </div>

      {/* Badge Container - Updated Design */}
      <div
        id="badge-container"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "800px",
          background: "white",
          boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          margin: "0 auto",
        }}
      >
        {/* LMS Logo at Left Corner */}
        <div
          style={{
            position: "absolute",
            top: "15px",
            left: "15px",
            zIndex: 10,
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/logo-icon.png`}
            alt="CoreIQPro"
            style={{
              width: "50px",
              height: "50px",
              objectFit: "contain",
            }}
          />
        </div>

        {/* Badge Content - Compact Layout */}
        <div
          style={{
            padding: "40px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {/* Badge Image */}
          <div
            style={{
              width: "280px",
              height: "280px",
              margin: "0 auto 15px auto",
              position: "relative",
            }}
          >
            <img
              src={badgeImage}
              alt="Achievement Badge"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
          </div>

          {/* Learner Name */}
          <Title
            level={2}
            style={{
              margin: "5px 0 5px 0",
              fontWeight: "bold",
              color: "#9A6B0E",
              fontSize: "28px",
            }}
          >
            {certificate.learner?.firstName} {certificate.learner?.lastName}
          </Title>

          {/* Simple Text */}
          <Text
            style={{
              fontSize: "16px",
              color: "#666",
              marginBottom: "5px",
            }}
          >
            has successfully completed
          </Text>

          {/* Course Title */}
          <Text
            strong
            style={{
              fontSize: "22px",
              color: "#9A6B0E",
              marginBottom: "5px",
            }}
          >
            {certificate.title}
          </Text>

          {/* Date */}
          <Text
            style={{
              fontSize: "14px",
              color: "#888",
              marginBottom: "25px",
            }}
          >
            Completed on {formatDate(certificate.issuedOn)}
          </Text>

          {/* Signatures */}
          <div
            style={{
              width: "100%",
              paddingTop: "15px",
              borderTop: "1px solid #E0E0E0",
            }}
          >
            <Row justify="center" gutter={[30, 10]}>
              <Col span={10} style={{ textAlign: "center" }}>
                <img
                  src={signature1}
                  alt="Signature 1"
                  style={{
                    width: "120px",
                    height: "auto",
                    marginBottom: "5px",
                    objectFit: "contain",
                  }}
                />
                <div style={{ marginTop: "5px" }}>
                  <Text style={{ fontSize: "14px", color: "#555" }}>
                    Program Director
                  </Text>
                </div>
              </Col>

              <Col span={10} style={{ textAlign: "center" }}>
                <img
                  src={signature2}
                  alt="Signature 2"
                  style={{
                    width: "120px",
                    height: "auto",
                    marginBottom: "5px",
                    objectFit: "contain",
                  }}
                />
                <div style={{ marginTop: "5px" }}>
                  <Text style={{ fontSize: "14px", color: "#555" }}>
                    Institution Head
                  </Text>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>

    </div>
  );
};

export default PublicCertificatePage;